import {
  isAwaitingScore,
  scoreTone,
} from "@/features/qualifications/utils/score-calculator";
import { UnsureNameMark } from "@/components/molecules/unsure-name-mark";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useScopedClient } from "@/features/clients/hooks/use-scoped-client";
import { useCustomerSummary } from "@/features/customers/hooks/use-customer-summary";
import {
  customerDisplayName,
  isNameUnsure,
} from "@/features/customers/name-verification";
import { useForm } from "@/features/qualifications/hooks/use-qualification-forms";
import type { Qualification } from "@/features/qualifications/types";
import { statusTone } from "@/features/utils/colors";
import { useDateFnsLocale, useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import { CircleNotchIcon } from "@phosphor-icons/react";
import { format, isThisYear } from "date-fns";

/** When a submission came in — what the list groups by when sorted by date. */
export const submittedAt = (qualification: Qualification) =>
  new Date(qualification.created_at || qualification.submitted_at || 0);

/**
 * One row of the submissions list: who, which form, where it stands, and its
 * score — small, at the end: the list is read by person, the score only orders it.
 */
export function SubmissionListItem({
  qualification,
  selected,
  checked,
  onSelect,
  onCheck,
  showDay,
}: {
  qualification: Qualification;
  selected: boolean;
  checked: boolean;
  onSelect: (qualification: Qualification) => void;
  onCheck: (qualification: Qualification, checked: boolean) => void;
  /** No day header above the row (sorted by score): the row carries its date. */
  showDay: boolean;
}) {
  const { t } = useTranslation("qualifications");
  const { clientId } = useScopedClient();
  const { data: customer } = useCustomerSummary(
    clientId,
    qualification.customer_id,
  );
  const { data: form } = useForm(qualification.form_id);
  const locale = useDateFnsLocale();

  const customerName =
    customerDisplayName(customer) || t("submissions.columns.unknown_customer");
  const date = submittedAt(qualification);
  const when = showDay
    ? format(date, isThisYear(date) ? "d MMM" : "d MMM yyyy", { locale })
    : format(date, "p", { locale });

  const awaiting = isAwaitingScore(qualification);
  const scored =
    !awaiting &&
    !!qualification.submitted_at &&
    qualification.score !== null &&
    qualification.score !== undefined;

  return (
    <div
      data-submission-id={qualification.id}
      className={cn(
        "flex items-stretch border-b border-l-2 transition-colors",
        selected
          ? "border-l-primary bg-slate-100 dark:bg-slate-900"
          : checked
            ? "border-l-transparent bg-primary/5"
            : "border-l-transparent hover:bg-slate-50 dark:hover:bg-slate-900",
      )}
    >
      <label className="flex cursor-pointer items-start py-3 pr-1 pl-3">
        <Checkbox
          checked={checked}
          onCheckedChange={(value) => onCheck(qualification, value === true)}
          aria-label={customerName}
          className="mt-0.5"
        />
      </label>
      <button
        type="button"
        onClick={() => onSelect(qualification)}
        aria-current={selected}
        className="min-w-0 flex-1 py-3 pr-3 pl-2 text-left"
      >
        <div className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-semibold">
            {customerName}
            {isNameUnsure(customer) && <UnsureNameMark />}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground">{when}</span>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
            {form?.name ?? "…"}
          </span>
          <span
            className={cn(
              "shrink-0 text-xs font-medium tabular-nums",
              scored
                ? scoreTone(qualification.score).text
                : "text-muted-foreground",
            )}
          >
            {awaiting ? (
              <CircleNotchIcon
                className="size-3.5 animate-spin"
                aria-label={t("submissions.view.scoring")}
              />
            ) : scored ? (
              `${Math.round(qualification.score * 100)}%`
            ) : (
              "—"
            )}
          </span>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <Badge
            color={statusTone(qualification.status) || "gray"}
            variant="outline"
          >
            {t(
              `submissions.statuses.${qualification.status}`,
              qualification.status,
            )}
          </Badge>
          {!qualification.completed && (
            <Badge variant="outline">
              {t("submissions.completion.incomplete")}
            </Badge>
          )}
        </div>
      </button>
    </div>
  );
}
